import { ComponentType } from 'react';

/**
 * The props every game component must accept.
 *
 * To add a new game:
 *   1. Create your component in src/components/games/MyGame.tsx implementing these props.
 *   2. Add one entry to the GAMES array below.
 *   That's it — the lobby, matchmaking, and in-game wiring are handled automatically.
 *
 * Multiplayer contract
 * --------------------
 * - `isHost`       — true for the player who created the room. Use this to decide who
 *                    runs authoritative logic (physics, timers, spawning, etc.).
 * - `sendGameState`— call this to broadcast any serialisable object to your opponent.
 *                    Listen for incoming state by subscribing to the window 'game_state'
 *                    CustomEvent: `window.addEventListener('game_state', e => e.detail)`.
 * - `onGameEnd`    — call with `'you'` (local player won), `'opponent'`, or `null` (draw/no winner).
 * - `isOffline`    — if true, no opponent is connected. Support single-player or hide multiplayer UI.
 *
 * Tips
 * ----
 * - Move all mutable game state into refs so your game loop doesn't suffer stale closures.
 * - Run your game loop as a single `setInterval` inside `useEffect(callback, [])`.
 * - The host should be authoritative for shared state (ball position, enemy list, etc.)
 *   and sync it to the guest each tick via sendGameState.
 */
export interface GameProps {
  onGameEnd: (winner: string | null) => void;
  sendGameState: (state: any) => void;
  isHost: boolean;
  gameId: string;
  isOffline?: boolean;
}

export interface GameDefinition {
  /** Unique string key used for routing and matchmaking. */
  id: string;
  name: string;
  description: string;
  /** Emoji or short string shown in the lobby card. */
  icon: string;
  players: string;
  component: ComponentType<GameProps>;
}

// ---------------------------------------------------------------------------
// Registry — add your game here
// ---------------------------------------------------------------------------

import TicTacToe from '../components/games/TicTacToe';
import Battleships from '../components/games/Battleships';
import Checkers from '../components/games/Checkers';
import Snake from '../components/games/Snake';
import CoopPong from '../components/games/CoopPong';
import WordDuel from '../components/games/WordDuel';
import SpaceShooter from '../components/games/SpaceShooter';
import TwentyQuestions from '../components/games/TwentyQuestions';

export const GAMES: GameDefinition[] = [
  {
    id: 'wordduel',
    name: 'Word Duel',
    description: 'Guess the hidden word first',
    icon: '📝',
    players: '2 Players',
    component: WordDuel,
  },
  {
    id: 'tictactoe',
    name: 'Tic Tac Toe',
    description: 'Classic 3x3 grid game',
    icon: '⭕',
    players: '2 Players',
    component: TicTacToe,
  },
  {
    id: 'battleships',
    name: 'Battleships',
    description: "Sink your opponent's fleet",
    icon: '🚢',
    players: '2 Players',
    component: Battleships,
  },
  {
    id: 'checkers',
    name: 'Checkers',
    description: 'Strategic board game',
    icon: '⚫',
    players: '2 Players',
    component: Checkers,
  },
  {
    id: 'snake',
    name: 'Snake Battle',
    description: 'Competitive snake game',
    icon: '🐍',
    players: '2 Players',
    component: Snake,
  },
  {
    id: 'spaceshooter',
    name: 'Space Shooter',
    description: 'Defend against aliens',
    icon: '🚀',
    players: '1-2 Players',
    component: SpaceShooter,
  },
  {
    id: 'cooppong',
    name: 'Coop Pong',
    description: 'Work together in Pong',
    icon: '🏓',
    players: '2 Players',
    component: CoopPong,
  },
  {
    id: 'twentyquestions',
    name: '20 Questions',
    description: 'Guess the secret in 20 yes/no questions',
    icon: '🤔',
    players: '2 Players',
    component: TwentyQuestions,
  },
];

/** Derive the GameType union from the registry so it never goes out of sync. */
export type GameId = (typeof GAMES)[number]['id'] | null;
